import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid2,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Slider, // 👈 add this
} from "@mui/material";
import UCVideoSlider from "./UCVideoSlider";
import DraggableVideoWrapper from "./DraggableVideo";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { IResultSet } from "./carouselHelpers";
// @ts-ignore
import CopyIframeUrlButton from "./CopyIframeUrlButton";
import SyncedVideo from "./SyncedVideo";

interface VideoFrameScrubberProps {
  filteredImages: IResultSet[];
  onFrameClick?: (videoUrl: string, currentTime: number) => void;
  initialTime?: number;
  inModal?: boolean;
}

const VideoFrameScrubber: React.FC<VideoFrameScrubberProps> = ({
  filteredImages,
  onFrameClick,
  initialTime = 0,
  inModal = false,
}) => {
  const videoUrls = filteredImages
    .filter(img => img.imageType?.includes("uc"))
    .sort((a, b) => (a.src.toLowerCase().includes("right") ? -1 : 1))
    .map(img => img.src);

  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  const singleVideoRef = useRef<HTMLVideoElement | null>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(initialTime);
  const [isLoaded, setIsLoaded] = useState(false);
  const [sliderPercent, setSliderPercent] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const hasMultipleVideos = videoUrls.length > 1;

  // convenience helpers
  const getVideos = () => videoRefs.current.filter(Boolean) as HTMLVideoElement[];
  const getPrimaryVideo = () => getVideos()[0];

  useEffect(() => {
    if (isPlaying || duration === 0) return;

    const newTime = (sliderPercent / 100) * duration;

    getVideos().forEach((v, i) => {
      v.currentTime = newTime;
    });

    setCurrentTime(newTime);
  }, [sliderPercent, duration, isPlaying]);


  // apply initialTime once loaded
  useEffect(() => {
    const videos = getVideos();
    if (!videos.length || !isLoaded || !duration || initialTime < 0) return;
    videos.forEach((v) => (v.currentTime = initialTime));
    setCurrentTime(initialTime);
    setSliderPercent((initialTime / duration) * 100);
  }, [isLoaded, duration, initialTime]);

  useEffect(() => {
    if (!hasMultipleVideos) {
      videoRefs.current[0] = singleVideoRef.current;
    }
  }, [hasMultipleVideos]);


  const handleFrameClick = (index: number) => {
    const clicked = videoRefs.current[index];
    if (!clicked || inModal || !onFrameClick) return;
    onFrameClick(clicked.src, clicked.currentTime);
  };

  const handleTimeUpdate = () => {
    const primary = getPrimaryVideo();
    if (!primary || duration === 0) return;

    // find which video triggered this update
    const index = videoRefs.current.findIndex((v) => v === primary);
    if (index !== -1) return;

    const time = primary.currentTime;
    setCurrentTime(time);
    setSliderPercent((time / duration) * 100);
  };



  const togglePlay = () => {
    const videos = getVideos();
    if (!videos.length) return;

    if (!hasMultipleVideos) {
      if (isPlaying) {
        // Pausing all videos
        videos.forEach((v) => {
          v.pause();
        });
      } else {
        videos.forEach((v) => {
          v.currentTime = currentTime;
          v.play();
        });
      }
    }

    setIsPlaying(!isPlaying);
  };

  const captureFrame = () => {
    const primary = getPrimaryVideo();
    if (!primary) return;
    const canvas = document.createElement("canvas");
    canvas.width = primary.videoWidth;
    canvas.height = primary.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(primary, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `frame-${Math.floor(currentTime * 1000)}ms.png`;
    link.click();
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        width: "100%",
        my: 0,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Video + Slider */}
      <Box sx={{ flexGrow: 1, overflow: "hidden" }}>
        <Grid2 container spacing={2} sx={{ height: "100%" }}>
          {/* Slider */}
          {/* Horizontal red progress bar (for multi-video mode) */}
          {!hasMultipleVideos && (
            // fallback to the old UCVideoSlider for single-video
            <Grid2 size={{ xs: inModal ? 2 : 3 }}>
              <UCVideoSlider
                sliderPercent={sliderPercent}
                onSliderChange={setSliderPercent}
                disabled={isPlaying}
              />
            </Grid2>
          )}


          {/* Video container */}
          <Grid2
            size={{ xs: hasMultipleVideos ? 10 : inModal ? 10 : 9 }}
            sx={{
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: inModal ? "solid 2px #999" : "none",
              borderRadius: "5px",
              height: "auto",
              maxHeight: "65vh",
              flexGrow: 1,
              position: "relative",
            }}
          >
            {/* Dual video or draggable single video */}
            {hasMultipleVideos ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  flexWrap: "nowrap",
                  gap: 2,
                  width: "auto",
                  height: "100%",
                  maxHeight: "65vh",
                  aspectRatio: "8/3",
                }}
              >
                {videoUrls.map((src, i) => (
                  <SyncedVideo
                    key={src}
                    src={src}
                    isPlaying={isPlaying}
                    currentTime={currentTime}
                    multiVid={true}
                    onLoaded={(duration) => {
                      if (i === 0) {
                        setDuration(duration);
                        setIsLoaded(true);
                      }
                    }}
                    onFrameClick={onFrameClick}
                    onPlayPauseChange={(playing) => setIsPlaying(playing)}
                    onTimeUpdate={handleTimeUpdate}
                    onEnded={() => {
                      const videos = getVideos();
                      videos.forEach((v) => {
                        if (!v) return;
                        v.pause();
                        v.currentTime = 0;
                      });
                      setIsPlaying(false);
                      setCurrentTime(0);
                      setSliderPercent(0);
                    }}
                    storeRef={(ref) => {
                      videoRefs.current[i] = ref;
                    }}
                  />
                ))}

              </Box>

            ) : inModal ? (
              // ✅ Draggable version for single video in modal
              <DraggableVideoWrapper
                videoRef={singleVideoRef}
                videoUrl={videoUrls[0]}
                currentTime={currentTime}
                onPause={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
                onEnded={() => {
                  setIsPlaying(false);
                  setSliderPercent(0);
                  setCurrentTime(0);
                  if (videoRefs.current[0])
                    videoRefs.current[0].currentTime = 0;
                }}
                onLoadedMetadata={() => {
                  const vid = videoRefs.current[0];
                  if (vid) {
                    setDuration(vid.duration);
                    setIsLoaded(true);
                  }
                }}
                onTimeUpdate={handleTimeUpdate}
                isPlaying={isPlaying}
              />
            ) : (
              // ✅ Regular single video (non-modal)
              <video
                ref={singleVideoRef}
                src={videoUrls[0]}
                crossOrigin="anonymous"
                onLoadedMetadata={() => {
                  const vid = videoRefs.current[0];
                  if (vid) {
                    setDuration(vid.duration);
                    setIsLoaded(true);
                  }
                }}
                onTimeUpdate={handleTimeUpdate}
                onClick={() => handleFrameClick(0)}
                controls={false}
                style={{
                  width: "100%",
                  maxHeight: "65vh",
                  objectFit: "contain",
                  borderRadius: 8,
                }}
                onPause={() => setIsPlaying(false)}
                onPlay={() => setIsPlaying(true)}
                onEnded={() => {
                  setIsPlaying(false);
                  setSliderPercent(0);
                  setCurrentTime(0);
                  if (videoRefs.current[0])
                    videoRefs.current[0].currentTime = 0;
                }}
              />
            )}
          </Grid2>
        </Grid2>
      </Box>

      {hasMultipleVideos && (
        <Box sx={{ display: "flex", width: "100%", justifyContent: "center", mt: 1 }}>
          <Slider
            value={sliderPercent}
            onChange={(_, newValue) => {
              console.log(newValue)
              if (typeof newValue !== "number" || duration === 0) return;

              setSliderPercent(newValue);

              const newTime = (newValue / 100) * duration;
              setCurrentTime(newTime);
            }}
            min={0}
            max={100}
            sx={{
              height: 3,
              width: "100%",
              "& .MuiSlider-track": { backgroundColor: "red" },
              "& .MuiSlider-thumb": {
                backgroundColor: "red",
                width: 12,
                height: 12,
                "&:hover": { boxShadow: "0 0 0 8px rgba(255,0,0,0.16)" },
              },
              "& .MuiSlider-rail": { backgroundColor: "red", opacity: 0.2 },
            }}
          />
        </Box>
      )}

      {/* Controls */}
      {isLoaded && (
        <Box
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
        >
          <Typography variant="body2" color="text.secondary">
            {`Time: ${currentTime.toFixed(2)}s / ${duration.toFixed(
              2
            )}s (${((currentTime / duration) * 100).toFixed(1)}%)`}
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="contained"
              onClick={togglePlay}
              size="small"
              sx={{ minWidth: "40px", padding: "6px" }}
            >
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </Button>
            <Button
              variant="contained"
              onClick={captureFrame}
              size="small"
              sx={{ minWidth: "40px", padding: "6px" }}
            >
              <CameraAltIcon />
            </Button>
            <CopyIframeUrlButton />
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default VideoFrameScrubber;

